import { FC, useState, KeyboardEvent, useRef, useEffect } from "react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { StoryblokRichtext } from "storyblok-rich-text-react-renderer";
import { StoryblokRichTextRenderer } from "@components";

export interface QAItemProps {
  question: string;
  answer: StoryblokRichtext | undefined | null;
  expandedByDefault?: boolean;
  _uid: string; // Storyblok unique ID for key prop
}

export const QAItem: FC<QAItemProps> = ({
  question,
  answer,
  expandedByDefault = false,
  _uid,
}) => {
  const [isOpen, setIsOpen] = useState(expandedByDefault);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    // Get the height of the content when it changes or when opened/closed
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen, answer]);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault(); // Prevent default space bar scrolling
      toggleOpen();
    }
  };

  return (
    <div className="border-b border-border last:border-b-0 overflow-hidden">
      <div
        className="flex justify-between items-center cursor-pointer py-4 px-2 select-none"
        onClick={toggleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={isOpen}
        aria-controls={`answer-${_uid}`}
      >
        <h3 className="text-base font-medium text-gray-800 dark:text-gray-200 pr-8">
          {question}
        </h3>
        <div className="flex-shrink-0 transition-transform duration-300 ease-in-out">
          {isOpen ? (
            <div className="bg-purple-100 dark:bg-purple-900/30 p-1.5 rounded-full">
              <HiChevronUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
          ) : (
            <div className="bg-gray-100 dark:bg-gray-800 p-1.5 rounded-full">
              <HiChevronDown className="h-5 w-5 text-muted" />
            </div>
          )}
        </div>
      </div>

      <div
        id={`answer-${_uid}`}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: isOpen ? `${contentHeight}px` : "0px",
          opacity: isOpen ? 1 : 0,
        }}
        aria-hidden={!isOpen}
      >
        <div
          ref={contentRef}
          className={`px-2 pb-5 ${isOpen ? "" : "absolute pointer-events-none"}`}
        >
          <div className={`rounded-md ${isOpen ? "animate-fadeIn" : ""}`}>
            <StoryblokRichTextRenderer document={answer} />
          </div>
        </div>
      </div>
    </div>
  );
};
